import { ServiceListItem } from '@spike/appointments-list-model';
import { FunctionComponent } from 'react';
import styles from './Service.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import InHomeTag from './InHomeTag';
import { faCircleXmark, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { PetEvaluationCategory, PetEvaluationStatus, PetEvaluationStatuses } from '@spike/pet-model';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

// Business area → evaluation category. Areas absent from the map never require an evaluation.
const evaluationCategoryByBusinessArea: Record<string, PetEvaluationCategory> = {
    grooming: 'grooming',
    dental: 'grooming',
    daycare: 'daycare',
    boarding: 'boarding'
};

interface ServiceProps {
    service: ServiceListItem;
    petId: number;
    marketplaceId?: number;
    showBusinessArea?: boolean;
    inHome?: boolean;
    className?: string;
}

export const Service: FunctionComponent<ServiceProps> = props => {
    const { organizationMarketplaces, getOrganizationMarketplace } = useOrganizationMarketplaces();

    const marketplaceName = props.marketplaceId && getOrganizationMarketplace(props.marketplaceId);
    const showMarketplaceName = organizationMarketplaces.length > 1 && marketplaceName;
    const evaluationStatuses = useSelector<RootState, PetEvaluationStatuses | undefined>(state =>
        state.pets.evaluationStatuses.find(pet => pet.petId === props.petId)
    );
    const serviceEvaluations =
        evaluationStatuses?.evaluations.filter(
            evaluation => evaluation.category === evaluationCategoryByBusinessArea[props.service.businessArea]
        ) ?? [];
    const failedEvaluations = serviceEvaluations.filter(evaluation => evaluation.status === 'failed');
    const missingEvaluations = serviceEvaluations.filter(evaluation => evaluation.status === 'missing');

    const evaluationLabel = (evaluation: PetEvaluationStatus): string =>
        evaluation.name || `${evaluation.category.charAt(0).toUpperCase()}${evaluation.category.slice(1)} Evaluation`;

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                {props.service.color && (
                    <div className={styles.left}>
                        <div
                            className={styles.color}
                            style={{ backgroundColor: props.service.color ?? undefined }}
                        ></div>
                    </div>
                )}
                <div className={styles.right}>
                    <span className={styles.name}>{props.service.name}</span>
                    {props.inHome && <InHomeTag />}
                    {props.showBusinessArea && (
                        <span className={styles.businessArea}>{props.service.businessArea}</span>
                    )}
                </div>
                {failedEvaluations.length > 0 && (
                    <Tooltip
                        content={
                            <div className={styles.tooltipContent}>
                                <span className={styles.tooltipTitle}>Evaluation</span>
                                {failedEvaluations.map(evaluation => (
                                    <span key={evaluation.category}>• Failed {evaluationLabel(evaluation)}</span>
                                ))}
                            </div>
                        }
                        className={styles.tooltip}
                    >
                        <FontAwesomeIcon
                            icon={faCircleXmark}
                            className={styles.failedEvaluationIcon}
                        />
                    </Tooltip>
                )}
                {missingEvaluations.length > 0 && (
                    <Tooltip
                        content={
                            <div className={styles.tooltipContent}>
                                <span className={styles.tooltipTitle}>Evaluation</span>
                                {missingEvaluations.map(evaluation => (
                                    <span key={evaluation.category}>Missing {evaluationLabel(evaluation)}</span>
                                ))}
                            </div>
                        }
                        className={styles.tooltip}
                    >
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className={styles.missingEvaluationIcon}
                        />
                    </Tooltip>
                )}
            </div>
            {showMarketplaceName && (
                <div className={styles.marketplaceContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <span className={styles.marketplaceName}>{marketplaceName.name}</span>
                </div>
            )}
        </div>
    );
};

export default Service;
